import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import toast from "react-hot-toast";
import ArrowForward from "~icons/material-symbols/arrow-forward";
import CheckCircle from "~icons/material-symbols/check-circle";
import ExpandMore from "~icons/material-symbols/expand-more";
import ProgressActivity from "~icons/material-symbols/progress-activity";
import { css } from "../../../../../styled-system/css";
import type { OsakiChargeBonusCampaignTerm } from "../../../../config";
import {
  type OsakiChargeBonusCampaignStatusLike,
  calculateOsakiChargeBonus,
  getOsakiChargeBonusCampaignAboutTitle,
} from "../../../../features/charge/osaki-charge-bonus-campaign";
import {
  primaryButtonCss,
  secondaryButtonCss,
} from "../../../../features/charge/styles";
import type { WalletClient } from "../../../../libs/client";
import { t } from "../../../../libs/i18n";
import { formatNumber } from "../../../../libs/utils";

type OsakiChargeBonusCampaignDetailProps = {
  client: WalletClient;
  status: OsakiChargeBonusCampaignStatusLike | null | undefined;
  canApply: boolean;
  campaignTerm: OsakiChargeBonusCampaignTerm;
  lotteryResultAnnouncementDateText: string;
  onApplied: () => Promise<void>;
};

export function getOsakiChargeBonusApplicationDisplayQuantity({
  status,
  hasApplied,
  selectedQuantity,
}: {
  status: OsakiChargeBonusCampaignStatusLike | null | undefined;
  hasApplied: boolean;
  selectedQuantity: number;
}) {
  if (hasApplied) {
    return selectedQuantity;
  }

  if (status?.isApplied) {
    return Number(status.maxQuantity) === 2 ? 2 : 1;
  }

  return selectedQuantity;
}

export function confirmOsakiChargeBonusApplication(quantity: number) {
  return window.confirm(
    t(
      "応募口数は{count}口です。\nこの内容で応募しますか？\n応募後は口数を変更できません。",
      {
        count: quantity,
      },
    ),
  );
}

export function OsakiChargeBonusCampaignDetail({
  client,
  status,
  canApply,
  campaignTerm,
  lotteryResultAnnouncementDateText,
  onApplied,
}: OsakiChargeBonusCampaignDetailProps) {
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showCompletedModal, setShowCompletedModal] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const isApplied = hasApplied || (status?.isApplied ?? false);
  const displayQuantity = getOsakiChargeBonusApplicationDisplayQuantity({
    status,
    hasApplied,
    selectedQuantity: quantity,
  });
  const fundSourceId = status?.fundSource?.id ?? "";
  const isApplyDisabled = !canApply || isApplied || loading;

  const refreshAppliedStatus = async () => {
    try {
      await onApplied();
    } catch {
      toast.error(t("応募は完了しましたが、最新状態の取得に失敗しました"));
    }
  };

  const apply = async () => {
    if (!fundSourceId) {
      toast.error(t("キャンペーン情報を取得できませんでした"));
      return;
    }

    const confirmed = confirmOsakiChargeBonusApplication(quantity);
    if (!confirmed) {
      return;
    }

    setLoading(true);
    try {
      await client.applyChargeBonusCampaign({
        fundSourceId,
        maxQuantity: BigInt(quantity),
      });
      setHasApplied(true);
      await refreshAppliedStatus();
      setShowCompletedModal(true);
    } catch (error) {
      if (error instanceof Error) {
        toast.error(`${t("応募に失敗しました")}\n[${error.message}]`);
        return;
      }
      toast.error(t("応募に失敗しました"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        className={css({
          display: "flex",
          flexDirection: "column",
          gap: "24px",
        })}
      >
        <section
          className={css({
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          })}
        >
          <h2
            className={css({
              fontSize: "20px",
              lineHeight: "1.35",
              fontWeight: "600",
              color: "text.primary",
            })}
          >
            {getOsakiChargeBonusCampaignAboutTitle(campaignTerm)}
          </h2>
          <p
            className={css({
              fontSize: "14px",
              lineHeight: "1.4",
              color: "text.secondary",
            })}
          >
            {t(
              "購入した金額に対して、大崎市内の店舗限定で利用可能な４割増のみやぎポイントを付与します。ポイントは加盟店全店で利用できる共通券ポイントと、小規模店舗のみで利用できる専用券ポイントが付与されます。１人につき最大２口までお申込み可能で、抽選で当選された方のみ購入できます。抽選結果は{date}にお知らせします。",
              { date: lotteryResultAnnouncementDateText },
            )}
          </p>
        </section>

        <PriceSummary quantity={1} />

        <section
          className={css({
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          })}
        >
          <h2
            className={css({
              fontSize: "20px",
              lineHeight: "1.35",
              fontWeight: "600",
              color: "text.primary",
            })}
          >
            {t("応募方法")}
          </h2>
          <p
            className={css({
              fontSize: "16px",
              lineHeight: "1.5",
              color: "text.primary",
            })}
          >
            {t(
              "下記のフォームに希望する購入口数を入力し、「応募する」ボタンを押します。",
            )}
          </p>
        </section>

        <section
          className={css({
            bg: "surface.primary",
            border: "1px solid",
            borderColor: "border.secondary",
            rounded: "16px",
            p: "16px",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          })}
        >
          <label
            htmlFor="osaki-charge-bonus-quantity"
            className={css({
              fontSize: "16px",
              lineHeight: "1.5",
              color: "text.primary",
            })}
          >
            {t("応募口数")}
          </label>
          <p
            className={css({
              fontSize: "14px",
              lineHeight: "1.4",
              color: "text.secondary",
            })}
          >
            {t("一口5,000円です。2口まで応募できます。")}
          </p>
          <div
            className={css({
              position: "relative",
              w: "109px",
            })}
          >
            <select
              id="osaki-charge-bonus-quantity"
              value={displayQuantity}
              disabled={isApplyDisabled}
              onChange={(event) => setQuantity(Number(event.target.value))}
              className={css({
                appearance: "none",
                w: "109px",
                h: "48px",
                bg: "surface.primary",
                border: "1px solid",
                borderColor: "border.secondary",
                rounded: "12px",
                px: "16px",
                color: isApplyDisabled ? "text.disable" : "text.primary",
                fontSize: "16px",
                lineHeight: "1.5",
                boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.03)",
              })}
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
            </select>
            <ExpandMore
              className={css({
                position: "absolute",
                right: "12px",
                top: "12px",
                color: "text.tertiary",
                pointerEvents: "none",
              })}
              fontSize={24}
            />
          </div>

          <ChargeBonusBreakdown quantity={displayQuantity} />
        </section>

        <div
          className={css({
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          })}
        >
          <button
            type="button"
            className={primaryButtonCss}
            disabled={isApplyDisabled}
            onClick={apply}
          >
            {loading ? (
              <ProgressActivity
                className={css({ animation: "spin 1s linear infinite" })}
              />
            ) : (
              t("応募する")
            )}
          </button>
          {isApplied && (
            <p
              className={css({
                color: "text.secondary",
                fontSize: "14px",
                lineHeight: "1.4",
              })}
            >
              {t("キャンペーンに応募済みです。")}
            </p>
          )}
          {!canApply && !isApplied && (
            <p
              className={css({
                color: "text.secondary",
                fontSize: "14px",
                lineHeight: "1.4",
              })}
            >
              {t("現在は応募期間外です。")}
            </p>
          )}
        </div>
      </div>

      {showCompletedModal && (
        <div
          className={css({
            position: "fixed",
            inset: 0,
            zIndex: 20,
            bg: "rgba(17, 24, 39, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: "20px",
          })}
        >
          <div
            className={css({
              bg: "surface.primary",
              rounded: "16px",
              w: "full",
              maxW: "350px",
              p: "24px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "24px",
            })}
          >
            <div
              className={css({
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "8px",
              })}
            >
              <p
                className={css({
                  fontSize: "16px",
                  fontWeight: "600",
                  lineHeight: "1.5",
                  color: "text.primary",
                })}
              >
                {t("応募が完了しました")}
              </p>
              <p
                className={css({
                  fontSize: "14px",
                  lineHeight: "1.4",
                  color: "text.secondary",
                  textAlign: "center",
                })}
              >
                {t("抽選結果の発表日までお待ちください。")}
              </p>
            </div>
            <CheckCircle
              className={css({
                color: "text.accentSuccess",
                w: "64px",
                h: "64px",
              })}
            />
            <button
              type="button"
              className={secondaryButtonCss}
              onClick={async () => {
                await refreshAppliedStatus();
                navigate({ to: "/" });
              }}
            >
              {t("閉じる")}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function PriceSummary({ quantity }: { quantity: number }) {
  const campaign = calculateOsakiChargeBonus(quantity);

  return (
    <section
      className={css({
        bg: "surface.primary",
        border: "1px solid",
        borderColor: "border.secondary",
        rounded: "16px",
        p: "16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "16px",
      })}
    >
      <div
        className={css({
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "12px",
        })}
      >
        <span className={summaryLabelCss}>{t("販売価格")}</span>
        <span className={css({ display: "flex", alignItems: "baseline" })}>
          <span className={summaryValueCss}>
            {formatNumber(campaign.price)}
          </span>
          <span className={summaryUnitCss}>{t("円")}</span>
        </span>
        <span className={summaryUnitCss}>{t("（税込）")}</span>
      </div>
      <ArrowForward
        className={css({ color: "text.tertiary", transform: "scaleX(1.4)" })}
        fontSize={32}
      />
      <div
        className={css({
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "8px",
        })}
      >
        <span className={summaryLabelCss}>{t("付与ポイント")}</span>
        <span className={css({ display: "flex", alignItems: "baseline" })}>
          <span
            className={css({
              fontSize: "24px",
              lineHeight: "1.35",
              color: "text.accentSuccess",
            })}
          >
            {formatNumber(campaign.totalPoint)}
          </span>
          <span className={summaryUnitCss}>pt</span>
        </span>
        <span
          className={css({
            color: "text.accentSuccess",
            fontSize: "12px",
            lineHeight: "1.3",
          })}
        >
          {t("（40%UP）")}
        </span>
      </div>
    </section>
  );
}

export function ChargeBonusBreakdown({ quantity }: { quantity: number }) {
  const campaign = calculateOsakiChargeBonus(quantity);

  return (
    <div
      className={css({
        bg: "surface.secondary",
        border: "1px solid",
        borderColor: "border.secondary",
        rounded: "8px",
        p: "20px",
        mt: "8px",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      })}
    >
      <BreakdownRow
        label={t("合計チャージ金額")}
        value={campaign.price}
        unit={t("円")}
      />
      <BreakdownRow
        label={t("専用券ポイント付与")}
        value={campaign.dedicatedPoint}
        unit="pt"
      />
      <BreakdownRow
        label={t("共通券ポイント付与")}
        value={campaign.commonPoint}
        unit="pt"
      />
    </div>
  );
}

function BreakdownRow({
  label,
  value,
  unit,
}: {
  label: string;
  value: number;
  unit: string;
}) {
  return (
    <div
      className={css({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "16px",
        color: "text.primary",
      })}
    >
      <span className={css({ fontSize: "14px", lineHeight: "1.4" })}>
        {label}
      </span>
      <span
        className={css({
          display: "flex",
          alignItems: "baseline",
          gap: "4px",
          whiteSpace: "nowrap",
        })}
      >
        <span className={css({ fontSize: "16px", lineHeight: "1.5" })}>
          {formatNumber(value)}
        </span>
        <span className={css({ fontSize: "14px", lineHeight: "1.4" })}>
          {unit}
        </span>
      </span>
    </div>
  );
}

const summaryLabelCss = css({
  fontSize: "14px",
  lineHeight: "1.4",
  color: "text.secondary",
});

const summaryValueCss = css({
  fontSize: "16px",
  lineHeight: "1.5",
  color: "text.primary",
});

const summaryUnitCss = css({
  fontSize: "12px",
  lineHeight: "1.3",
  color: "text.tertiary",
});
